import { MoneyHashPayments } from '@kitopi-com/payments';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isSplitDefined } from '@/api/getSplitDetails';
import { API_EVENTS_KEY } from '@/constants/config';
import {
  VITE_APP_ENV,
  VITE_WEBHOOK_BASE_URL,
  VITE_WEBHOOK_SPLIT_BASE_URL,
} from '@/constants/constants';
import { CheckoutProvider, useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useBrand } from '@/hooks/useBrand';
import { useLanguage } from '@/hooks/useLanguage';
import { useRestrictedPaymentMethods } from '@/hooks/useRestrictedPaymentMethods';
import { useSplitState } from '@/hooks/useSplitState';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import type { Bill as BillDetailsType } from '@/interfaces/Bill';
import { monitoring, setPaymentStartTimestamp } from '@/screens/Checkout/monitoring';
import { ServiceFeeBottomSheetInfo } from '@/screens/Checkout/ServiceFeeBottomSheetInfo';
import { useBranding } from '@/utils/branding';
import { calculateFinalTipAmount } from '@/utils/tipCalculations';
import styles from '../../screens/Checkout/Checkout.module.scss';
import { Bill } from '../Bill/Bill';
import BrandPresentation from '../BrandPresentation/BrandPresentation';
import InfoAlert from '../InfoAlert/InfoAlert';
import { useOutletKitchen } from '../LinkToMenu/useOutletKitchen';
import PaymentsErrorBoundary from '../PaymentsErrorBoundary';
import { Rewards } from '../Rewards/Rewards';
import TipsSkeleton from '../Skeletons/TipsSkeleton';
import { SplitBill } from '../SplitBill/SplitBill';
import { SplitButton } from '../SplitButton/SplitButton';
import { SummaryController } from '../Summary/SummaryController';
import Terms from '../Terms/Terms';
import { Tips } from '../Tips/Tips';
import { useCheckoutMonitoring } from './useCheckoutMonitoring';
import { useEqualSplitPayment } from './useEqualSplitPayment';
import { useItemsSplitPayment } from './useItemsSplitPayment';
import { usePaymentNavigation } from './usePaymentNavigation';
import { useShareBill } from './useShareBill';
import { useSplitAutoJoin } from './useSplitAutoJoin';

interface Props {
  bill?: BillDetailsType;
  isBillError?: boolean;
  isLoading?: boolean;
  paymentComponentRef: React.RefObject<HTMLDivElement | null>;
  recheckBill: () => Promise<{ orderHasBeenChanged: boolean }>;
  onPaymentPhaseChange?: (isActive: boolean) => void;
}

const SplitExpiredNotice = ({
  orderNumber,
  remainingAmount,
}: {
  orderNumber: string;
  remainingAmount?: number;
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.splitExpiredContainer}>
      <h2 style={{ color: 'red' }}>{t('checkout.checkoutController.splitExpired')}</h2>
      <div className={styles.splitExpiredDetails}>
        <p>{t('checkout.checkoutController.orderNumberVar', { orderNumber })}</p>
        <p>{t('checkout.checkoutController.remainingAmountVar', { remainingAmount })}</p>
        <p>{t('checkout.checkoutController.contactRestaurant')}</p>
      </div>
    </div>
  );
};

const CheckoutControllerInternal = (props: Props) => {
  const { t } = useTranslation();
  const {
    bill,
    isLoading: isLoadingBill,
    isBillError,
    paymentComponentRef,
    recheckBill,
    onPaymentPhaseChange,
  } = props;
  const { queryParams } = useValidateQueryParams();
  const { outletId } = queryParams();
  const { data: brandData } = useBrand();
  const sessionId = useRef(crypto.randomUUID()).current;
  const { currency, country } = useOutletKitchen(outletId);
  const { showRewards } = useBranding();
  const restrictedMethods = useRestrictedPaymentMethods();

  const {
    state,
    actions: { initBill, setPaymentMethod, toggleServiceFeeInfo, toggleSplitBill, setProcessing },
  } = useCheckoutContext();
  const {
    baseToPay,
    selectedTipPercentage,
    tipToPay,
    numberOfSplits,
    payingFor,
    isServiceFeeInfoOpen,
    customTipAmount,
    splitType,
  } = state;
  const isItemsSplitActive = splitType === 'SPLIT_BY_ITEMS';

  // Feature Flags
  const isRecheckBillFeatureEnabled =
    new URLSearchParams(window.location.search).get('recheckBill') === 'true';

  const onPaymentMethodChange = useCallback(
    (paymentMethod: string) => {
      setPaymentMethod(paymentMethod);
    },
    [setPaymentMethod]
  );

  const { language } = useLanguage();

  useEffect(() => {
    if (bill) {
      initBill(bill.baseTotal, bill.tipAmount || 0);
    }
  }, [bill, initBill]);

  const appEnv = VITE_APP_ENV === 'local' || !VITE_APP_ENV ? 'dev' : VITE_APP_ENV;

  const {
    isLoading: isSplitLoading,
    isSplitExist,
    splitDetails,
    isSplitExpired,
  } = useSplitState({
    orderNumber: bill?.orderNumber || '',
    enableSmartPolling: true,
  });

  useSplitAutoJoin({ isSplitExist, splitDetails });

  const {
    onBeforeItemsPayment,
    buildItemsLineItems,
    showItemsConflictAlert,
    showItemsSplitCreationFailedAlert,
  } = useItemsSplitPayment({
    bill,
    currency,
    splitDetails,
    isItemsSplitActive,
    isSplitExist,
    isSplitLoading,
  });

  const { showEqualSplitCreationFailedAlert } = useEqualSplitPayment({
    bill,
    currency,
    numberOfSplits: state.numberOfSplits,
    isSplitExist,
    isSplitLoading,
  });

  const brandName = brandData?.name || 'Kitopay';

  const {
    splitAmount,
    chargeAmount,
    serviceFee,
    estimatedCashback,
    signupBonusEligible,
    yourShare,
    isLoadingBillForSplit,
    isServiceFeePending,
  } = useShareBill({
    bill,
    baseToPay,
    selectedTipPercentage,
    numberOfSplits,
    payingFor,
    isItemsSplitActive,
    isProcessing: state.isProcessing,
  });

  const isLoading = isLoadingBill || isLoadingBillForSplit;

  useCheckoutMonitoring({ bill, isBillError, isLoading, isSplitExist, yourShare, sessionId });

  const { onPaymentResult } = usePaymentNavigation({
    bill,
    isBillError,
    brandName,
    estimatedCashback,
    signupBonusEligible,
    isItemsSplitActive,
    numberOfSplits,
    payingFor,
    setProcessing,
  });

  const webhookUrl = useMemo(() => {
    // An items-split payment is a split payment — same self-notification hook as equal
    if (state.numberOfSplits > 0 || isItemsSplitActive) {
      return `${VITE_WEBHOOK_SPLIT_BASE_URL}/api/internal/self-notifications/`;
    }
    return `${VITE_WEBHOOK_BASE_URL}/api/dine-in-public/payments-api/bills/${bill?.billId}/payments`;
  }, [state.numberOfSplits, isItemsSplitActive, bill?.billId]);

  const onBeforePayment = useCallback((): Promise<{ orderHasBeenChanged: boolean }> => {
    // Set payment start timestamp when payment begins
    setPaymentStartTimestamp();

    if (isItemsSplitActive) {
      return Promise.resolve(onBeforeItemsPayment());
    }

    if (isRecheckBillFeatureEnabled) {
      return recheckBill();
    }

    return Promise.resolve({ orderHasBeenChanged: false });
  }, [isRecheckBillFeatureEnabled, onBeforeItemsPayment, isItemsSplitActive, recheckBill]);

  const onPaymentStatusChange = useCallback(
    (status: string) => {
      // Advance to processing, and return to idle only on 'failed'. Success and
      // pending timeout are handled by onPaymentResult; 'initiated' is never used as
      // an idle signal to avoid stale-closure races after onResult.
      // The SDK does not forward canceled / failed results (e.g. dismissed Apple Pay
      // sheet, onBeforePayment rejection) to onResult — 'failed' is the only signal.
      if (status === 'processing') {
        setProcessing(true);
      } else if (status === 'failed') {
        setProcessing(false);
      }
    },
    [setProcessing]
  );

  useEffect(() => {
    onPaymentPhaseChange?.(state.isProcessing);
  }, [state.isProcessing, onPaymentPhaseChange]);

  const isSummaryWithTipsVisible = bill && !isBillError && tipToPay !== null;
  const isBillActive = bill && !isBillError;
  const isEqualSplitActive = state.numberOfSplits > 0;
  // Neither split mode offers a pay button until its split is registered on the
  // backend — an intent against an unregistered split would orphan the payment.
  // The widget also waits for the share's service fee (GK-552), but not for a
  // reload that only re-prices the tip — that would remount it on every tip tap.
  const isPaymentReady =
    (!isItemsSplitActive || isSplitExist) &&
    (!isEqualSplitActive || isSplitExist) &&
    !isServiceFeePending;

  const getIntentData = useCallback(() => {
    if (!bill) {
      throw new Error('Bill is not available');
    }

    if (isItemsSplitActive) {
      return {
        referenceId: bill.orderNumber,
        webhookUrl,
        lineItems: buildItemsLineItems(),
      };
    }

    return {
      referenceId: bill.orderNumber,
      webhookUrl,
    };
  }, [bill, webhookUrl, isItemsSplitActive, buildItemsLineItems]);

  return (
    <>
      {(showItemsSplitCreationFailedAlert || showEqualSplitCreationFailedAlert) && (
        <InfoAlert message={t('checkout.infoAlert.splitCreationFailed')} />
      )}
      {showItemsConflictAlert && <InfoAlert message={t('checkout.splitByItems.conflictAlert')} />}
      <div className={styles.orderInfo}>
        <div>
          <BrandPresentation orderId={bill?.orderId || ''} />
          <Bill />
        </div>

        {!bill && isLoading && <TipsSkeleton />}

        {isSummaryWithTipsVisible && (
          <>
            <Tips orderNumber={bill.orderNumber} orderId={bill.orderId} />

            {/* The summary computes the tip itself and only needs the share's fee, so a
                tip-only reload (fee still known) must not swap it for the skeleton */}
            <SummaryController
              isLoading={isLoadingBill || isServiceFeePending}
              isSplitExist={isSplitExist}
              grossDiscount={bill.grossDiscount || 0}
              serviceFee={serviceFee}
              orderNumber={bill.orderNumber}
              openServiceFeeInfo={toggleServiceFeeInfo}
            />
          </>
        )}
      </div>

      {isBillActive && (
        <>
          <div ref={paymentComponentRef}>
            <div className={styles.paymentContainer}>
              <h2>{t('checkout.checkoutController.payments')}</h2>
              {isSplitExpired ? (
                <SplitExpiredNotice
                  orderNumber={bill.orderNumber}
                  remainingAmount={splitDetails?.remainingAmount}
                />
              ) : (
                <PaymentsErrorBoundary monitoring={monitoring}>
                  {!isSplitLoading && baseToPay > 0 && isPaymentReady && (
                    <MoneyHashPayments
                      paymentSource="KITOPAY_WEB"
                      sessionId={sessionId}
                      bizEventsApiKey={API_EVENTS_KEY}
                      amount={chargeAmount}
                      tipAmount={calculateFinalTipAmount(
                        customTipAmount,
                        splitAmount,
                        selectedTipPercentage
                      )}
                      currency={currency}
                      getIntentData={getIntentData}
                      countryCode={country}
                      appEnv={appEnv}
                      onResult={onPaymentResult}
                      onPaymentStatusChange={onPaymentStatusChange}
                      buttonContainerId="money-hash-button-container"
                      kitchenId={bill.kitchenId}
                      serviceFee={serviceFee || 0}
                      onMonitoring={monitoring}
                      onPaymentMethodChange={onPaymentMethodChange}
                      onBeforePayment={onBeforePayment}
                      isCardEnabled={true}
                      restrictedCards={['Amex']}
                      restrictedMethods={restrictedMethods}
                      locale={language}
                    />
                  )}
                </PaymentsErrorBoundary>
              )}
              <Terms />

              {!isSplitExpired && (
                <>
                  {showRewards && (
                    <div className={styles.rewardsContainer}>
                      <Rewards
                        isLoading={isLoading || false}
                        estimatedCashback={estimatedCashback}
                        signupBonusEligible={signupBonusEligible}
                      />
                    </div>
                  )}
                  <div className={styles.splitButtonContainer}>
                    <SplitButton
                      onClick={toggleSplitBill}
                      orderId={bill?.orderId}
                      isSplitExist={isSplitExist}
                    />
                    <div
                      className={styles.payButtonContainer}
                      id="money-hash-button-container"
                    ></div>
                  </div>
                </>
              )}
            </div>
          </div>
          <SplitBill
            orderId={bill.orderId}
            orderNumber={bill.orderNumber}
            numberOfSplits={isSplitDefined(splitDetails) ? splitDetails.numberOfSplits : undefined}
            isItemsSplitDisabled={bill.paymentOptionsRestricted === true}
          />

          {isServiceFeeInfoOpen && <ServiceFeeBottomSheetInfo onClick={toggleServiceFeeInfo} />}
        </>
      )}
    </>
  );
};

// Main exported component that wraps the internal component with the provider
export const CheckoutController = (props: Props) => {
  return (
    <CheckoutProvider>
      <CheckoutControllerInternal {...props} />
    </CheckoutProvider>
  );
};
